import {
  useAllSamples,
  useCanCall,
  useDispenseRecords,
  useMe,
  useModules,
  useMonitoringTrays,
} from '@ctcells/api'
import { dataSourceFor, isModuleKey } from '@ctcells/core'
import { errorMessage, t } from '@ctcells/i18n'
import { useStreamState } from '@ctcells/realtime'

import {
  DEMO_CHAMBER_ROWS,
  DEMO_MODULES,
  DEMO_SAMPLES,
  DEMO_TRAY_LAYOUT,
  DEMO_VISION_FEEDS,
} from '../demoData.ts'
import { activeModules } from '../moduleService.ts'
import { useScreenAccess } from '../screenAccess.ts'
import { ScreenLoading, ScreenState } from '../ScreenState.tsx'

import { ChamberStepContext } from '../../figures/chamber3d/chamberRegistry.ts'
import { useActiveBatchSource } from '../../shell/activeBatchSource.ts'
import { useStripBatch } from '../../shell/stripPick.ts'

import { runningChamberStep } from './chamberMotion.ts'
import { currentDispensePass } from './chamberProgress.ts'
import { useRunningDeviceKey } from './followRunningDevice.ts'
import { chamberRows } from './monitoringModel.ts'
import { samplesOfBatch } from './sampleFlowModel.ts'
import { useSupplyReplenish } from './supplyReplenish.ts'
import { supplySummary } from './supplySummary.ts'
import { StainLatestWellContext, TrayViewContext } from './trayViewModel.ts'
import { MonitoringScreen, type MonitoringScreenProps } from './MonitoringScreen.tsx'
import { useSampleFlow } from './useSampleFlow.ts'
import { ModuleEventLog } from './ModuleEventLog.tsx'

/**
 * 운영 › 모니터링 컨테이너.
 *
 * 🔴 **화면은 0줄도 안 바뀐다.** 컨테이너가 훅을 부르고 props 로 내려줄 뿐이다.
 * 🔴 **목/실물이 갈리는 지점은 여기 하나다**(`dataSourceFor('modules')`). 어댑터·MSW 로 내리지 않는다.
 *
 * 분류: **1. 실제로 연결** — `GET /api/v1/modules` 실물 4건이 흐른다(2026-09-17 확인).
 * ⚠ 다만 카드의 조작 버튼은 실물이 `allowed_commands: []` 를 주므로 **정상적으로 전부 비활성**이다.
 *   모니터링 화면에는 애초에 제어 버튼이 없다(§1-3) — 조작은 개발 › 제어에서만 한다.
 *
 * 🔴 **주 자료와 부가 자료를 가른다**(2026-09-21).
 * | 자료 | 경로 | 실패하면 |
 * | --- | --- | --- |
 * | **주** | `GET /modules` | 화면을 막는다 — 이것이 없으면 그릴 것이 없다 |
 * | 부가 | `GET /samples` | **시료 칸만** 접고 사유를 적는다. 화면은 그대로 그린다 |
 *
 * 경계 다섯
 * | 로딩 | 스켈레톤 대신 「불러오는 중」. 버튼 없음                       |
 * | 에러 | `errorMessage()` — 코드·규칙 ID 를 화면에 노출하지 않는다       |
 * | 빈   | 모듈 0건이면 화면이 빈 카드 줄을 그린다(표가 0행이면 빈 목록이 정답) |
 * | 권한 | `useScreenAccess()` → 숨기지 않고 비활성 + 사유                 |
 * | 끊김 | 같은 `access.controlDisabled`. `gate()` 가 1순위로 막는다        |
 */
export type MonitoringContainerProps = Pick<
  MonitoringScreenProps,
  /* 장비 관점 콘솔이 켜는 세 가지(`leading` · `overview` · `gantry`)도 그대로 통과시킨다 */
  | 'deviceKey'
  | 'onSelectDevice'
  | 'onFollowDevice'
  | 'chambers'
  | 'tray'
  | 'visionFeeds'
  | 'leading'
  | 'overview'
  | 'gantry'
  /* 오른쪽 서랍 — 무엇을 꽂을지는 라우트가 정한다. 화면이 선택기 줄 아래에 세운다 */
  | 'drawer'
  /* 🔴 **[시료][장비] 두 탭으로 가는 스위치.** 안 넘기면 종전 그림 탭 배치다 */
  | 'layout'
> & {
  /**
   * **시료 흐름을 켠다 — 시료 관점 콘솔만**(eric 지시 2026-09-21).
   *
   * 🔴 **`MonitoringScreenProps.sampleFlow` 와 «다른 것»이다.** 저쪽은 그릴 값 한 벌이고
   *    이쪽은 **스위치**다 — 값을 만드는 일(작업 고르기 · 시료 거르기 · 셈)은 컨테이너의 몫이라
   *    바깥에서 넘기지 않는다. 라우트가 아는 것은 「이 관점은 시료 흐름을 본다」뿐이다.
   * ⚠ 기본 꺼짐 = 장비 관점 그대로. 그쪽은 이 프롭을 안 넘긴다.
   */
  sampleFlow?: boolean
  /** 시료를 누르면 갈 곳. 경로를 아는 것은 라우트다(화면·컨테이너는 라우터를 모른다) */
  onOpenSample?: ((sampleId: string) => void) | undefined
  /** 오른쪽 판에 열린 시료. 그 카드가 「고른 카드」로 선다(eric 2026-09-27) */
  selectedSampleId?: string | undefined
  /**
   * **통합 콘솔의 소모품 판을 켠다.** 장비 관점은 안 넘긴다. 염색기 모듈(deviceOnly)은 별도로 요약을 켠다(2026-10-25).
   * 🔴 칩 · 판 머리는 눌러지지 않는다(eric 2026-10-02 「안 나오게 해줘」 — 상세 팝오버를 걷었다). 팁 · TC 의 「보충」 버튼만
   *    확인 창을 바로 연다 — 그 판정은 이 컨테이너가 `useSupplyReplenish` 로 짓는다.
   */
  supplies?: boolean
}

/** 컨테이너가 «덧붙여» 내려주는 축. 바깥에서 넘기는 값이 아니다 */
type MonitoringInnerProps = MonitoringContainerProps &
  Pick<MonitoringScreenProps, 'runningDevice' | 'moduleEvents'>

/**
 * 🔴 **「지금 도는 모듈」을 읽는 자리는 여기 하나다**(eric 지시 2026-09-21 — 「염색기가 실행
 *    중이면 염색기 탭이 활성화」). 시료 관점 콘솔 · 장비 관점 콘솔이 **같은 컨테이너**를 쓰므로
 *    한 번만 붙이면 둘 다 된다. 화면(`MonitoringScreen`)은 이 값을 «받기만» 한다.
 * ⚠ 태블릿은 아직 컨테이너를 거치지 않고 화면을 직접 꽂는다(`tabletViews.tsx` — 권한도
 *   자리표시자다). 그쪽은 거기서 같은 훅을 부른다.
 */
export function MonitoringContainer(props: MonitoringContainerProps) {
  const runningDevice = useRunningDeviceKey()
  const withRunning = {
    ...props,
    ...(props.layout === 'sampleTabs'
      ? { moduleEvents: <ModuleEventLog device="all" /> }
      : props.layout === 'deviceOnly' &&
          props.deviceKey !== undefined &&
          isModuleKey(props.deviceKey)
        ? { moduleEvents: <ModuleEventLog device={props.deviceKey} /> }
        : {}),
    ...(runningDevice === undefined ? {} : { runningDevice }),
  }
  return dataSourceFor('modules') === 'demo' ? (
    <MonitoringDemo {...withRunning} />
  ) : (
    <MonitoringLive {...withRunning} />
  )
}

/**
 * 🔴 **데모 경로가 fixture 를 «명시적으로» 넘긴다**(2026-09-20). 종전에는 아무것도 안 넘기고
 *    화면의 기본값(`= DEMO_*`)에 기댔는데, **그 기본값이 live 경로에서도 살아났다** —
 *    모듈이 전부 `IDLE` 인 화면의 로터에 `S-1045` 같은 가짜 시료가 돌고 있었다(실측).
 *    ⇒ 기본값을 지우고 **줄 사람이 주게** 했다. 목이 새는 길이 이것 하나였다.
 */
function MonitoringDemo({
  sampleFlow,
  onOpenSample,
  selectedSampleId,
  supplies,
  ...props
}: MonitoringInnerProps) {
  const access = useScreenAccess()
  /* 목 모드 — 보충 버튼은 서되 비활성 + 「목 모드」 사유(fixture 화면이 실물 서버에 쓰지 않는다) */
  const replenishFor = useSupplyReplenish(false)
  /*
   * 🔴 **훅은 조건 없이 부른다.** 쓸지 말지는 아래에서 고른다 — `sampleFlow` 가 꺼졌다고
   *    호출을 건너뛰면 훅 차례가 렌더마다 달라진다.
   * ⚠ demo 도 **같은 규율**을 탄다. fixture 시료가 fixture 작업(`B-260911-02`)에 매여 있어
   *   작업 목록이 demo 일 때만 격자가 찬다 — 그것이 사실이다(도는 작업이 없으면 빈 격자다).
   */
  const flow = useSampleFlow({
    samples: DEMO_SAMPLES,
    /* 🔴 **fixture 화면이 실물 서버를 때리지 않는다.** demo 시료 ID 에는 보고서가 없다 */
    skipTimes: true,
    ...(props.runningDevice === undefined ? {} : { runningDevice: props.runningDevice }),
    ...(onOpenSample === undefined ? {} : { onOpenSample }),
    ...(selectedSampleId === undefined ? {} : { selectedSampleId }),
  })
  const demoSummary = supplySummary({
    modules: DEMO_MODULES,
    reagents: flow.deckReagents,
    labware: flow.deckLabware,
  })
  const supplySet =
    supplies === true ||
    (props.layout === 'deviceOnly' && (props.deviceKey === 'stain' || props.deviceKey === 'asm'))
      ? { summary: demoSummary, replenish: replenishFor(demoSummary, DEMO_MODULES) }
      : undefined
  return (
    <MonitoringScreen
      chambers={DEMO_CHAMBER_ROWS}
      modules={DEMO_MODULES}
      tray={DEMO_TRAY_LAYOUT}
      visionFeeds={DEMO_VISION_FEEDS}
      {...props}
      access={access}
      {...(sampleFlow === true ? { sampleFlow: flow } : {})}
      {...(supplySet === undefined ? {} : { supplies: supplySet })}
    />
  )
}

/** `GET /samples` 를 한 번에 받는 상한. ⚠ `batch_id` 필터가 계약에 없다(BR-15) */

function MonitoringLive({
  sampleFlow,
  onOpenSample,
  selectedSampleId,
  supplies,
  ...props
}: MonitoringInnerProps) {
  const access = useScreenAccess()
  const modules = useModules({})
  const replenishFor = useSupplyReplenish(true)
  /*
   * 🔴 **못 부르는 조회는 쏘지 않는다**(2026-09-26 · viewer). `GET /samples` 는 operator/admin 전용이라
   *    viewer 는 403 이 확정이다 — 쏘고 접는 대신 **안 쏘고 사유를 적는다.**
   * 🔴 3값이다: `operations` 를 **모르면(null) 쏜다** · 알고 `listSamples` 가 없으면 안 쏜다.
   *    모를 때 막으면 로그인 직후 한 프레임 시료 판이 빈다. 그때 403 이 오면 종전대로 그 구역만 접는다.
   */
  const { operations } = useMe()
  const canListSamples = useCanCall('listSamples')
  const samplesLocked =
    operations !== null && !canListSamples ? t('monitoring.samplesLocked') : undefined
  /* 🔴 **전부** 받는다(`useAllSamples` 머리말) — 앞 200건만 받으면 도는 작업의 시료가 목록 끝에 있어 한 건도 안 온다 */
  const samples = useAllSamples({ skip: samplesLocked !== undefined })
  /*
   * 🔴 **챔버 표·장비 카드의 시료는 «지금 도는 작업»의 것만이다**(eric 2026-09-22 「챔버가 너무 많아 — 원심분리기
   *    부분」). `GET /samples` 는 이 DB 의 시료 전부(실측 269건)를 주고, 서버가 종착·중단 뒤에도 `current_module` 을
   *    비우지 않아(`samplesAt` 머리말) 앞 회차의 시료 22건이 여전히 「원심분리기에 있다」로 남아 있었다. 작업 줄·시료
   *    판이 이미 쓰는 같은 축(`samplesOfBatch`)으로 좁힌다 — 도는 작업이 없으면 챔버도 없다.
   */
  const { batches } = useActiveBatchSource()
  // 🔴 작업 줄과 같은 «고른 작업»(`stripChoiceStore`)
  const batchId = useStripBatch(batches)?.batch_id
  /* 끊겼으면 모듈의 「지금」을 모른다 — 챔버 3D 세부 국면을 세우지 않는다 */
  const eventsStale = useStreamState('events').stale
  /*
   * 트레이(C·A·L) · 장비 안 챔버 뷰(`GET /monitoring/trays` · eric 2026-10-07) — 도는 작업이 있을 때만 한 번 받고, 그 뒤는
   * 장비 상태 · 시료 단계 · 스텝 완료 이벤트가 다시 받게 한다(`stream/bridge.ts` · 폴링 없음). 그림이 `TrayViewContext` 로 읽는다.
   */
  const trayView = useMonitoringTrays({ batchId, skip: batchId === undefined })

  /** 시료를 못 본 사유. `undefined` 면 **봤다**는 뜻이고 그때만 수를 적는다 */
  const chambersNotice =
    samplesLocked !== undefined
      ? samplesLocked
      : samples.isPending
        ? t('state.loading')
        : samples.isError
          ? errorMessage(samples.error)
          : undefined

  /*
   * 🔴 **조기 반환보다 «위»에 둔다.** 훅은 렌더마다 같은 차례로 불려야 한다 — 아래
   *    `if (modules.isPending) return` 뒤에 두면 모듈이 오는 순간 훅 개수가 바뀐다.
   * ⚠ 그래서 `chambersNotice` 계산도 여기까지 올라왔다(`samples` 하나만 보므로 안전하다).
   */
  const flow = useSampleFlow({
    samples: samples.data?.items,
    /* 배치 추적을 못 받았을 때만 챔버 자리 근거로 쓴다(`useSampleFlow` · 새 요청 아님 — 그림이 받은 응답) */
    trayView: trayView.data,
    ...(chambersNotice === undefined ? {} : { notice: chambersNotice }),
    ...(props.runningDevice === undefined ? {} : { runningDevice: props.runningDevice }),
    ...(onOpenSample === undefined ? {} : { onOpenSample }),
    ...(selectedSampleId === undefined ? {} : { selectedSampleId }),
  })

  /*
   * 🔴 **주 자료는 `GET /modules` 하나다. 그것만 화면을 막는다.**
   *
   * ⚠ **실제 사고(2026-09-21 · eric 이 `dev-viewer` 로 붙어 찾았다):** 여기에
   *   `if (samples.isError) return <ScreenState …>` 가 있었고, `GET /samples` 는
   *   **operator/admin 전용**이라(`app/views/samples.py` `READ_ROLES = WRITE_ROLES` ·
   *   계약 `listSamples` `x-required-role` — `patient_id` 직접 노출 PII 접촉면)
   *   viewer 에게 403 `INSUFFICIENT_ROLE` 이다. 그 **덧붙는 숫자 하나**가 실패했다고
   *   모듈 카드·그림·비전 탭이 **전부** 「권한이 없습니다」로 덮였다 —
   *   viewer 는 `monitor` 권한이 있어 **모니터링을 볼 수 있어야 하는 역할**인데도.
   *
   * ⇒ 규율: **부가 조회의 실패는 그 구역만 접는다.** 시료는 ⓐ 카드의 「시료 N건」과
   *   ⓑ 사이드 패널 챔버 표, 두 자리에만 쓰인다. 둘 다 `chambersNotice` 로 사유를 받는다.
   * 🔴 **「0건」으로 접지 마라.** `chambers={[]}` 만 넘기면 카드가 수를 안 적고 표가
   *   「없음」이라고 단언해 **「이 장비에 시료가 없다」는 거짓**이 된다. 「못 봤다」와 「없다」는
   *   다르고, 그 구분을 나르는 것이 `chambersNotice` 다.
   */
  /*
   * 염색기 «직전 분주 칸» — 정면도의 피펫 툴이 갈 칸(`StainLatestWellContext`). 🔴 폴링이 아니다 — 분주 완료 프레임에만 다시 받는다
   * (`stream/bridge.ts` `isDispenseComplete`). 🔴 훅이라 조기 반환(`modules.isPending`)보다 앞에 둔다 · 부가 조회라 실패하면 칸만 모른다
   */
  /* 🔴 분주 기록은 admin · operator 만(서버 `PRIVILEGED_ROLES`) — viewer 는 부르지 않는다(분주 완료마다 403 이 되풀이됐다 · 2026-10-12) */
  const canListDispense = useCanCall('listDispenseRecords')
  const dispense = useDispenseRecords({
    ...(trayView.data?.batch?.batch_id === undefined
      ? {}
      : { batch_id: trayView.data.batch.batch_id }),
    limit: 50,
    skip: trayView.data?.batch?.started !== true || eventsStale || !canListDispense,
  })
  const stainLatestWell = dispense.isSuccess
    ? currentDispensePass(dispense.data.items).keys().next().value
    : undefined
  /*
   * ⚠ 시료 카드 챔버의 스포이드 · 「분주 중」(피펫이 «지금» 든 웰)은 걷었다(eric 2026-10-14 「장비쪽 드리퍼랑 피펫 빼자」 → 추천안) — 자리가
   *   서버 좌표가 아니라 스텝 · 분주 기록으로 짐작한 것이라 실장비에서 실제와 어긋난다. `StainPipetteWellContext` 를 싣지 않아 기본값(`null`)이다.
   */

  if (modules.isPending) return <ScreenLoading />
  if (modules.isError) return <ScreenState text={errorMessage(modules.error)} tone="error" />

  /*
   * 🔴 **폐기 모듈을 걷어 넘긴다**(2026-09-20). 그대로 넘기면 카드 격자에 **같은 종류가 두 장**
   *    뜨고 하나는 「응답 없음」이다 — 고장과 폐기가 구분되지 않는다.
   * ⚠ 거르는 층은 **컨테이너**다. 화면은 받은 목록을 그대로 그린다(`findModule` 도 같은 축을
   *   한 번 더 보지만, 그건 그 함수가 «거르지 않은» 배열을 받을 수도 있어서다).
   *
   * 🔴 **`tray` 와 `visionFeeds` 를 «일부러» 안 넘긴다.**
   *    - `tray` — 서버에 시료↔칸 대응이 없다(`ChamberRow` 머리말 · BR-31). 안 넘기면 화면이
   *      빈 격자 + 「칸 배정 정보 없음」을 그린다. **fixture 로 채우면 그것이 곧 거짓이다**
   *    - `visionFeeds` — 카메라 축이 계약에 0건이다(BR-40). fixture 판정을 여기로 들이면
   *      **없는 판정을 발명**하게 된다
   *    ⚠ 이 두 줄이 «없는 것»이 이 컨테이너의 핵심이다. 「비어 보인다」고 채우지 마라.
   *
   * ⚠ **옛 주석 정정(2026-09-21).** 여기에 「`visionFeeds` 가 없으면 비전 탭이 아예 붙지 않는다」가
   *   적혀 있었는데 **지금은 틀린 말이다.** 탭은 언제나 붙고(eric 지시 2026-09-21) 비는 것은 탭 «안»이다.
   *   그리고 그 탭 안은 이제 **스트림이 설정돼 있으면 실영상**으로 찬다 — `visionFeeds` 를 넘기지
   *   않는 것이 곧 「live 다」라는 선언이고, 실영상은 그 자리에만 선다(`FocusFigure` 의 `liveStream`).
   */
  /*
   * 🔴 **챔버 3D 의 세부 국면**(어셈블러 UV 등 · `chamberMotion.ts`). 주 자료(`GET /modules`)에서 바로 읽는다 — 새 조회 없음.
   *    카드 격자 · 서랍 · 장비 탭이 전부 이 화면 «안»에 그려져 같은 값 하나를 받는다.
   */
  const chamberStep = runningChamberStep({
    modules: modules.data.items,
    batchId,
    stale: eventsStale,
  })
  const liveSummary = supplySummary({
    modules: modules.data.items,
    reagents: flow.deckReagents,
    labware: flow.deckLabware,
  })
  const supplySet =
    supplies === true ||
    (props.layout === 'deviceOnly' && (props.deviceKey === 'stain' || props.deviceKey === 'asm'))
      ? { summary: liveSummary, replenish: replenishFor(liveSummary, modules.data.items) }
      : undefined
  return (
    <ChamberStepContext value={chamberStep}>
      <TrayViewContext value={trayView.data}>
        <StainLatestWellContext value={stainLatestWell}>
          <MonitoringScreen
            {...props}
            access={access}
            /* 🔴 **못 봤으면 빈 배열이고, 그 «이유»는 `chambersNotice` 가 나른다**(위 주석) */
            chambers={
              samples.data === undefined
                ? []
                : chamberRows(samplesOfBatch(samples.data.items, batchId))
            }
            chambersNotice={chambersNotice}
            modules={activeModules(modules.data.items)}
            {...(sampleFlow === true ? { sampleFlow: flow } : {})}
            {...(samplesLocked === undefined ? {} : { samplesLocked })}
            /* viewer — 작업의 배정으로 카드가 섰으면 잠김 판 대신 카드를 그린다(`useSampleFlow` 의 배정 갈래) */
            samplesViaAssignments={flow.bundles.some(
              (b) => b.placeholder !== true && b.sampleId !== undefined,
            )}
            /*
             * 🔴 **소모품 요약 — 주 자료(`GET /modules`) + 이미 받은 레시피만 쓴다.** 새 조회를 더하지 않는다 —
             *    그래서 요약이 viewer 에게도 그대로 선다. ⚠ lot · 분주 기록은 상세 팝오버(2026-10-02 걷음)만 부르던 것이라
             *    이제 이 화면 어디서도 부르지 않는다.
             */
            {...(supplySet === undefined ? {} : { supplies: supplySet })}
          />
        </StainLatestWellContext>
      </TrayViewContext>
    </ChamberStepContext>
  )
}
